import React, { useMemo } from 'react';

import {
  normalizeSelectValue,
  ToggleButton,
  ToggleButtonGroup,
  Select,
} from '@gogame/components';
import { FormField, getIn, omitFormProps } from '@gogame/form';
import { withFormField } from '@gogame/form';

import { ColorLabelTextListItem } from 'scenes/Campaigns/components/shared';
import {
  loadAffiliates,
  loadApplications,
  loadNetworks,
  loadPlatforms,
  loadLocales,
  loadCountries,
  loadAudiences,
  loadConversionPageFlows,
  loadConversionPages,
  loadLabels,
  loadLinkFormats,
  loadActiveUsers,
  loadLocations,
  loadRewardPackages,
  loadSubLocations,
  loadPromoCodes,
  loadCampaignActivityTypes,
  loadMediaSources,
  loadInitialTrackRe,
  loadDomains,
  loadTargetUsers,
  loadSpendAllocationType,
} from 'scenes/Campaigns/sources/fields';

import {
  CAMPAIGN_NAME,
  CAMPAIGN_ALTERNATIVE_ID,
  GENERATE_NAME_AUTOMATICALLY,
} from './Campaign/constants';
import { DEEP_LINK_SUB_LOCATION_SHOP } from './DeepLink';
import {
  DEEP_LINK_REWARD_PACKAGES,
  DEEP_LINK_END_DATE,
  DEEP_LINK_LOCATION_IN_GAME,
  DEEP_LINK_SUB_LOCATION,
  DEEP_LINK_MULTI_REDEMPTIONS,
  DEEP_LINK_MAX_REDEMPTIONS,
} from './DeepLink';
import { ConversionPageOption, MultiChangeSelect } from './components';
import {
  AFFILIATE,
  APPLICATION,
  NETWORK,
  PLATFORM,
  LOCALE,
  CONVERSION_PAGE_FLOW,
  CONVERSION_PAGES,
  COUNTRY,
  LABELS,
  LINK_FORMAT_TYPE,
  OWNER,
  PROMO_CODE,
  CAMPAIGNS,
  TYPE,
  RE_ENGAGEMENT,
  MEDIA_SOURCE,
  CONVERSION_PAGE_ID,
  LINK_EXPIRATION_DATE,
  PARTNER_NAME,
  DOMAIN,
  NAME,
  SPEND_ALLOCATION_TYPE,
} from './constants';
import {
  DEEP_LINK_AUDIENCES,
  DEEP_LINK_TARGET_USERS,
  DEEP_LINK_TARGET_USERS_ALL_USERS,
  DEEP_LINK_TARGET_USERS_AUDIENCE,
} from './DeepLink';
import { DEPENDENCIES } from './dependencies';

const renderConversionPageOption = (option) => {
  return <ConversionPageOption {...option} />;
};

const renderLabelOption = ({ name, color }) => (
  <ColorLabelTextListItem name={name} title={name} color={color} />
);

const affiliateIdsWithOutLandings = [
  273, //pl: SEO
  3528, //pl: Funnel Emails
  3607, //pl: Plarium Play
  3518, //pl: FB - Player Migration
  3520, //pl: Youtube Channel Organic-PC
];

const APPLICATION_RAID = 27;

const PLATFORM_STEAM = 25;

const isRequiredConversionPages = ({ application, platform, affiliate }) => {
  return !(
    (application === APPLICATION_RAID && platform === PLATFORM_STEAM) ||
    affiliateIdsWithOutLandings.includes(affiliate)
  );
};

const validateConversionPages = (value) => {
  // TODO: after discussion with Dmitriy we decide to hardcode
  //  excepted affiliates that makes "Conversion pages" non required.
  //  It is SRS requirements. It is better to clarify this business
  //  logic with actuality.
  if (value === undefined) {
    return 'Conversion pages is required';
  }
};

export const validateParentCampaignName = (value) => {
  if (value == null) {
    return 'Parent campaign name is required';
  }
};

const keepAffiliate = (v) => v;

export const CampaignNameField = ({
  platformName,
  generateNameAutomatically,
}) => (
  <FormField.TextField
    name={CAMPAIGN_NAME}
    label={`${platformName ? `${platformName} c` : 'C'}ampaign name`}
    padding={'0'}
    required={true}
    helper={'GG ID will be populated automatically'}
    disabled={generateNameAutomatically}
  />
);

export const AutoNameSwitchField = () => (
  <FormField.Switch
    name={GENERATE_NAME_AUTOMATICALLY}
    label={'Generate name automatically'}
    basis={0}
    padding={0}
  />
);

export const CampaignPartnerInfoField = ({
  platformName,
  campaignIndexField = 0,
  disabled,
}) => (
  <FormField.TextField
    name={`${CAMPAIGNS}[${campaignIndexField}].${CAMPAIGN_ALTERNATIVE_ID}`}
    label={`${platformName} Alternative campaign name`}
    padding={'0'}
    disabled={disabled}
  />
);

export const LabelsField = (props) => (
  <FormField.MultiSelect
    name={LABELS}
    label={'Labels'}
    padding={'0'}
    source={loadLabels}
    lazy={true}
    renderOption={renderLabelOption}
    {...props}
  />
);

export const PromoCodeField = ({
  lazy = true,
  source = loadPromoCodes,
  ...props
}) => (
  <FormField.Select
    name={PROMO_CODE}
    label={'Promo code'}
    padding={'0'}
    source={source}
    depends={DEPENDENCIES[PROMO_CODE]}
    lazy={lazy}
    withDeselectButton={true}
    {...props}
  />
);

const normalizeLinkFormat = (value, options) => {
  if (value === undefined) {
    return options[0].id;
  }
  return normalizeSelectValue(value, options);
};
export const LinkFormatField = ({ disabled }) => (
  <FormField.Select
    name={LINK_FORMAT_TYPE}
    padding={'0'}
    source={loadLinkFormats}
    disabled={disabled}
    normalizeValue={normalizeLinkFormat}
    withDeselectButton={true}
  />
);

export const OwnerField = ({ disabled }) => (
  <FormField.Select
    withDeselectButton={true}
    name={OWNER}
    label={'Owner'}
    padding={'0'}
    source={loadActiveUsers}
    disabled={disabled}
  />
);

export const AffiliateField = ({ disabled }) => (
  <FormField.Select
    name={AFFILIATE}
    label={'Affiliate'}
    source={loadAffiliates}
    isDenyEdit={true}
    padding={'0'}
    required={true}
    normalizeValue={keepAffiliate}
    disabled={disabled}
    withDeselectButton={true}
  />
);

export const ApplicationField = ({ disabled }) => (
  <FormField.Select
    name={APPLICATION}
    label={'Application'}
    source={loadApplications}
    isDenyEdit={true}
    padding={'0'}
    required={true}
    depends={DEPENDENCIES[APPLICATION]}
    disabled={disabled}
    withDeselectButton={true}
  />
);

export const SimpleNetworkField = ({ disabled }) => (
  <FormField.Select
    name={NETWORK}
    label={'Network'}
    source={loadNetworks}
    padding={'0'}
    required={true}
    depends={DEPENDENCIES[NETWORK]}
    disabled={disabled}
    withDeselectButton={true}
  />
);

export const MultiChangeNetworkField = ({ onMultiChange }) => (
  <MultiChangeSelect
    name={NETWORK}
    label={'Network'}
    source={loadNetworks}
    isDenyEdit={true}
    padding={'0'}
    required={true}
    onMultiChange={onMultiChange}
    depends={DEPENDENCIES[NETWORK]}
    lazy={true}
    withDeselectButton={true}
  />
);

export const SimplePlatformField = ({
  disabled,
  withDeselectButton = true,
  source = loadPlatforms,
}) => (
  <FormField.Select
    name={PLATFORM}
    label={'Platform'}
    source={source}
    isDenyEdit={true}
    padding={'0'}
    required={true}
    depends={DEPENDENCIES[PLATFORM]}
    disabled={disabled}
    withDeselectButton={withDeselectButton}
  />
);

export const MultiChangePlatformField = ({ onMultiChange }) => (
  <MultiChangeSelect
    name={PLATFORM}
    label={'Platform'}
    source={loadPlatforms}
    isDenyEdit={true}
    padding={'0'}
    required={true}
    onMultiChange={onMultiChange}
    withDeselectButton={true}
    depends={DEPENDENCIES[PLATFORM]}
    helper={'Separate campaign will be created for each platform'}
  />
);

export const CpDomainField = () => (
  <FormField.Select
    withDeselectButton={true}
    name={DOMAIN}
    label={'CP domain'}
    source={loadDomains}
    depends={DEPENDENCIES[DOMAIN]}
    padding={'0'}
    normalizeValue={normalizeCpDomain}
  />
);

function normalizeCpDomain(v, options) {
  if (v === undefined) {
    return options.find((opt) => opt.isDefault)?.id;
  }
  return v;
}

export const LocaleField = () => (
  <FormField.Select
    withDeselectButton={true}
    name={LOCALE}
    label={'CP locale'}
    source={loadLocales}
    padding={'0'}
    depends={DEPENDENCIES[LOCALE]}
  />
);

export const ConversionPageFlowsField = () => {
  return (
    <FormField.Select
      name={CONVERSION_PAGE_FLOW}
      label={'CP flows'}
      source={loadConversionPageFlows}
      padding={'0'}
      withAll={false}
      withDeselectButton={true}
      depends={DEPENDENCIES[CONVERSION_PAGE_FLOW]}
    />
  );
};

export const ConversionPagesField = ({ application, platform, affiliate }) => {
  const required = isRequiredConversionPages({
    application,
    platform,
    affiliate,
  });

  return (
    <FormField.MultiSelect
      name={CONVERSION_PAGES}
      label={'Conversion pages'}
      source={loadConversionPages}
      padding={'0'}
      required={required}
      renderOption={renderConversionPageOption}
      validate={required ? validateConversionPages : null}
      depends={DEPENDENCIES[CONVERSION_PAGES]}
      withAll={false}
      searchExp={true}
      groupBy={null}
    />
  );
};

export const CountryField = ({ disabled }) => (
  <FormField.Select
    required={true}
    name={COUNTRY}
    label={'Country'}
    source={loadCountries}
    padding={'0'}
    lazy={true}
    placeholder={'All'}
    disabled={disabled}
    withDeselectButton={true}
    isDenyEdit={true}
  />
);
function normalizeTargetUser(targetUserId) {
  if (targetUserId === undefined) {
    return 0;
  }
  return targetUserId;
}
export const DeepLinkTargetUsersField = ({ disabled, validate, required }) => (
  <FormField.Select
    name={DEEP_LINK_TARGET_USERS}
    label={'Target users'}
    source={loadTargetUsers}
    depends={DEPENDENCIES[DEEP_LINK_TARGET_USERS]}
    isOptionDisabled={(option) => option.isDisabled}
    padding={'0'}
    disabled={disabled}
    required={required}
    validate={validate}
    normalizeValue={normalizeTargetUser}
  />
);

const validateDeepLinkAudiencesField = (value) => {
  if (value?.length > 0) {
    return undefined;
  }
  return 'Audiences is required';
};

export const DeepLinkAudiencesField = ({ disabled }) => (
  <FormField.MultiSelect
    name={DEEP_LINK_AUDIENCES}
    label={'Audiences'}
    source={loadAudiences}
    depends={DEPENDENCIES[DEEP_LINK_AUDIENCES]}
    disabled={disabled}
    validate={validateDeepLinkAudiencesField}
    padding={'0'}
    lazy={true}
    _cleanOnUnmount={true}
    required={true}
  />
);

export const DeepLinkRewardPackagesField = ({ disabled }) => (
  <FormField.MultiSelect
    name={DEEP_LINK_REWARD_PACKAGES}
    label={'Reward packages'}
    source={loadRewardPackages}
    padding={'0'}
    disabled={disabled}
    depends={DEPENDENCIES[DEEP_LINK_REWARD_PACKAGES]}
  />
);

export const DeepLinkLocationField = ({ disabled }) => {
  return (
    <FormField.Select
      name={DEEP_LINK_LOCATION_IN_GAME}
      label={'Location in game'}
      required={true}
      source={loadLocations}
      depends={DEPENDENCIES[DEEP_LINK_LOCATION_IN_GAME]}
      padding={'0'}
      withDeselectButton={true}
      disabled={disabled}
      normalizeValue={(v, data) => {
        if (data?.length === 1) {
          return data[0].id;
        }
        return v;
      }}
    />
  );
};

export const DeepLinkSubLocationField = ({ locationInGame, disabled }) => {
  const isDisabled = useMemo(() => {
    return locationInGame !== DEEP_LINK_SUB_LOCATION_SHOP;
  }, [locationInGame]);

  return (
    <FormField.Select
      name={DEEP_LINK_SUB_LOCATION}
      label={'Sub location'}
      source={loadSubLocations}
      padding={'0'}
      lazy={true}
      depends={DEPENDENCIES[DEEP_LINK_SUB_LOCATION]}
      disabled={isDisabled || disabled}
      withDeselectButton={true}
    />
  );
};

export const DeepLinkEndDateField = ({ disabled }) => (
  <FormField.DatePicker
    name={DEEP_LINK_END_DATE}
    label={'Expiration date'}
    padding={'0'}
    required={true}
    disabled={disabled}
  />
);

const normalizeValueDeepLinkMultipleRedemptions = (value, params) => {
  if (!params[DEEP_LINK_REWARD_PACKAGES].length) {
    return false;
  }
  if (params[DEEP_LINK_TARGET_USERS] === DEEP_LINK_TARGET_USERS_ALL_USERS) {
    return false;
  }
  if (params[DEEP_LINK_TARGET_USERS] === DEEP_LINK_TARGET_USERS_AUDIENCE) {
    return false;
  }
  return value;
};
const sourceDeepLinkMultipleRedemptions = (params) => params;

export const DeepLinkMultipleRedemptions = ({ disabled }) => (
  <FormField.Checkbox
    label={'Multiple redemptions'}
    name={DEEP_LINK_MULTI_REDEMPTIONS}
    source={sourceDeepLinkMultipleRedemptions}
    padding={'0'}
    depends={DEPENDENCIES[DEEP_LINK_MULTI_REDEMPTIONS]}
    normalizeValue={normalizeValueDeepLinkMultipleRedemptions}
    disabled={disabled}
  />
);

const validateDeepLinkMaxRedemptions = (v) => {
  const label = 'Max redemptions per player';
  const min = 2;
  if (v !== undefined && v < 2) {
    return `${label} is less than ${min}`;
  }
};
export const DeepLinkMaxRedemptions = ({ value, onChange, name }) => (
  <FormField.TextField
    value={value}
    onChange={onChange}
    data-cy={name}
    label={'Max redemptions per player'}
    name={DEEP_LINK_MAX_REDEMPTIONS}
    padding={'0'}
    type={'number'}
    _cleanOnUnmount={true}
    validate={validateDeepLinkMaxRedemptions}
  />
);

export const TypeField = ({ disabled }) => (
  <FormField.Select
    name={TYPE}
    label={'Campaign type'}
    padding={'0'}
    source={loadCampaignActivityTypes}
    disabled={disabled}
  />
);

export const TrackREField = ({ onValidate, active }) => {
  return (
    <FormField.Checkbox
      name={RE_ENGAGEMENT}
      label={'Track RE'}
      padding={'0'}
      active={active}
      validate={onValidate}
      depends={DEPENDENCIES[RE_ENGAGEMENT]}
      source={active && loadInitialTrackRe}
      normalizeValue={(_, { trackReInitial }) => {
        return trackReInitial;
      }}
    />
  );
};

export const MediaSourceField = ({ onAdd }) => (
  <FormField.Select
    name={MEDIA_SOURCE}
    source={loadMediaSources}
    label={`Media source`}
    addOptionLabel={'Media source'}
    padding={'0'}
    required={true}
    onAdd={onAdd}
  />
);

export const ButtonsField = withFormField({
  padding: 0,
  name: 'linkType',
})(({ linksConfig, value, onChange, disabled }) => {
  return (
    <ToggleButtonGroup
      color={'primary'}
      value={value?.id || value}
      enforceValue={true}
      exclusive={true}
      onChange={onChange}
      disabled={disabled}
      padding={0}
    >
      {linksConfig?.map(({ isAllowed, type }) => {
        return !type.id ? null : (
          <ToggleButton
            value={type.id}
            disabled={!isAllowed || disabled}
            key={type.id}
          >
            {type.name}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
});

export const ConversionPageField = ({
  label = 'Conversion page',
  source,
  dependencies,
  disabled,
}) => {
  return (
    <FormField.Select
      name={CONVERSION_PAGE_ID}
      label={label}
      padding={0}
      source={source}
      lazy={true}
      depends={[...DEPENDENCIES[CONVERSION_PAGE_ID], ...dependencies]}
      disabled={disabled}
      groupBy={null}
    />
  );
};

export const SelectedDateField = ({ disabled }) => (
  <FormField.DatePicker
    name={LINK_EXPIRATION_DATE}
    label={'Expiration date'}
    minDate={new Date()}
    disabled={disabled}
    padding={0}
  />
);

export const PartnerNameField = ({ label = 'Partner Name', disabled }) => (
  <FormField.TextField
    padding={0}
    label={label}
    name={PARTNER_NAME}
    debounceTimeout={300}
    disabled={disabled}
  />
);

export const ParentCampaignNameField = () => (
  <FormField.TextField
    name={NAME}
    label={'Parent campaign name'}
    validate={validateParentCampaignName}
    required={true}
    padding={0}
  />
);

function spendAllocationTypeParams(values, depends) {
  return {
    costModelTypes: depends.map((p) => {
      return getIn(values, p);
    }, []),
  };
}

export const SpendAllocationType = withFormField({
  name: SPEND_ALLOCATION_TYPE,
  getOptionValue: (option) => option.id || 0,
  params: spendAllocationTypeParams,
  source: loadSpendAllocationType,
  normalizeValue: (value, options) => {
    return options.length === 1
      ? options[0].id
      : normalizeSelectValue(value, options);
  },
})(({ area, disabled, ...props }) => {
  return (
    <Select
      {...omitFormProps(props)}
      label={'Spend allocation model'}
      loading={area.loading}
      options={area.data}
      disabled={area.data?.length === 1 || disabled}
    />
  );
});
